/** Pages of a memory list (thing search, a bundle, sources): fetched again when `key` changes, debounced while
 * someone types, every answer but the newest dropped. A null `key` clears the list. */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Page } from "@/api/memory-focus";
export interface PagedQuery<T> {
  items: T[];
  hasMore: boolean;
  loading: boolean;
  error: boolean;
  settled: boolean;
  loadMore: () => void;
  retry: () => void;
}
interface PagedState<T> {
  key: string | null;
  items: T[];
  cursor: string | null;
  loading: boolean;
  error: boolean;
  settled: boolean;
}
const EMPTY: PagedState<never> = {
  key: null,
  items: [],
  cursor: null,
  loading: false,
  error: false,
  settled: false,
};
export function usePagedQuery<T>(
  key: string | null,
  load: (cursor?: string) => Promise<Page<T>>,
  debounceMs = 200,
): PagedQuery<T> {
  const loader = useRef(load);
  useLayoutEffect(() => {
    loader.current = load;
  });
  const request = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<PagedState<T>>(EMPTY);
  const fetchPage = useCallback((forKey: string, cursor?: string) => {
    const id = ++request.current;
    setState((current) => ({ ...current, loading: true, error: false }));
    loader.current(cursor).then(
      (page) => {
        if (id === request.current)
          setState((current) => ({
            key: forKey,
            items: cursor ? [...current.items, ...page.items] : page.items,
            cursor: page.pagination.next_cursor,
            loading: false,
            error: false,
            settled: true,
          }));
      },
      () => {
        if (id === request.current)
          setState((current) => ({
            ...current,
            key: forKey,
            loading: false,
            error: true,
            settled: true,
          }));
      },
    );
  }, []);
  useLayoutEffect(() => {
    request.current += 1;
    setState({ ...EMPTY, key, loading: key !== null });
    return () => {
      request.current += 1;
    };
  }, [key, debounceMs]);
  useEffect(() => {
    if (key === null) return;
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      fetchPage(key);
    }, debounceMs);
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    };
  }, [debounceMs, fetchPage, key]);
  const current = state.key === key;
  const visible = current ? state : EMPTY;
  return {
    items: visible.items,
    hasMore: visible.cursor !== null,
    loading: key !== null && (!current || visible.loading),
    error: visible.error,
    settled: key !== null && current && visible.settled && !visible.loading,
    loadMore: () => {
      if (key !== null && state.key === key && state.cursor && !state.loading)
        fetchPage(key, state.cursor);
    },
    retry: () => {
      if (key === null) return;
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
      request.current += 1;
      setState({ ...EMPTY, key, loading: true });
      fetchPage(key);
    },
  };
}
